import { Ref, forwardRef, useMemo } from "react"
import { Form, Formik, FormikProps } from "formik"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { User } from "../../../../../users/userTypes"
import { CreateUpdateTenantUserContent } from "./CreateUpdateTenantUserContent"
import { useLocationState } from "../../../../../../core/router/routerHooks"
import * as Yup from "yup"
import { isPossiblePhoneNumber } from "libphonenumber-js"
import { CreateEditTenantUserFormValues, CreateOrUpdateUserState } from "../tenantUserTypes"

type CreateOrEditTenantUserFormProps = Readonly<{
    existingUser?: User
    onSubmit: (formData: CreateEditTenantUserFormValues) => void
}>
type FormikRef = Ref<FormikProps<CreateEditTenantUserFormValues>>

export const CreateOrEditTenantUserForm = forwardRef(
    ({ onSubmit, existingUser }: CreateOrEditTenantUserFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()

        const { user: userItem } = useLocationState<CreateOrUpdateUserState>({ user: undefined })
        const mode = userItem ? "update" : "create"

        function getInitialValues(userState?: CreateOrUpdateUserState): CreateEditTenantUserFormValues {
            return {
                username: userState?.user?.username ?? "",
                employeeId: userState?.user?.employeeId ?? "",
                displayName: userState?.user?.displayName ?? "",
                email: userState?.user?.email ?? "",
                mobileNumber: userState?.user?.mobileNumber ?? "",
                phoneNumber: userState?.user?.phoneNumber ?? "",
                sendEmailInvitation: true,
                ignoreUserSyncJobs: userState?.user?.settings?.ignoreUserSyncJobs ?? false,
                isActive: userState?.user?.isActive ?? true,
                workgroups: userState?.user?.workgroups ?? [],
            }
        }

        function getValidationSchema() {
            return Yup.object().shape({
                username: Yup.string(),
                employeeId: Yup.string(),
                displayName: Yup.string().required(l10n.getString("office-manager-create-user-required-field-empty")),
                email: Yup.string()
                    .email(l10n.getString("office-manager-create-user-invalid-email-address"))
                    .required(l10n.getString("office-manager-create-user-required-field-empty")),
                mobileNumber: Yup.string().test({
                    name: "is-valid-phonenumber",
                    test: (value) => (value ? isPossiblePhoneNumber(value, "DK") : true),
                    message: l10n.getString("office-manager-create-user-invalid-phone-number"),
                }),
                phoneNumber: Yup.string().test({
                    name: "is-valid-phonenumber",
                    test: (value) => (value ? isPossiblePhoneNumber(value, "DK") : true),
                    message: l10n.getString("office-manager-create-user-invalid-phone-number"),
                }),
                sendEmailInvitation: Yup.bool(),
                ignoreUserSyncJobs: Yup.bool(),
                isActive: Yup.bool(),
                workgroups: Yup.array(),
            })
        }

        const initialValues = useMemo(() => getInitialValues({ user: existingUser }), [existingUser])
        const validationSchema = useMemo(() => getValidationSchema(), [])

        function handleSubmit(values: CreateEditTenantUserFormValues) {
            onSubmit(values)
        }

        return (
            <Formik<CreateEditTenantUserFormValues>
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                innerRef={ref}
            >
                <Form>
                    <CreateUpdateTenantUserContent existingUser={existingUser} mode={mode} />
                </Form>
            </Formik>
        )
    },
)

